// Montaje y firma del `CaptureBundle` (ADR 0004 §6, producto/05 §7.5). Solo se incluyen las
// claves del contrato (`strictObject`): nada de extras, nada de `undefined`.
import type { Burst } from './FrameRing.js';
import { shotChainRoot } from './HashChain.js';
import { bundleMessage, shotMessage, type CaptureSigner } from './SessionKeys.js';
import type {
  CaptureBundle,
  CaptureChallengeEvent,
  CaptureDeclarations,
  CaptureDevice,
  CaptureSdk,
  CaptureShot,
  CaptureVideo,
} from './types.js';

/** Disparo firmado a partir de una ráfaga: cadena (§4.1) y firma `kyp.capture.shot.v1`. */
export async function buildShot(
  sessionId: string,
  serverNonce: string,
  index: number,
  burst: Burst,
  signer: CaptureSigner,
): Promise<CaptureShot> {
  const frames = burst.frames.map((f) => f.meta);
  const chain_root = await shotChainRoot(sessionId, serverNonce, index, frames);
  const unsigned = { index, chosen_frame: burst.chosen_frame, frames, chain_root };
  return {
    ...unsigned,
    signature: await signer.sign(shotMessage(sessionId, serverNonce, unsigned)),
  };
}

export interface BundleParts {
  sessionId: string;
  sdk: CaptureSdk;
  device: CaptureDevice;
  publicKey: string;
  shots: CaptureShot[];
  videos: CaptureVideo[];
  scheduleId: string;
  events: CaptureChallengeEvent[];
  declarations: CaptureDeclarations;
}

/** Bundle firmado (`kyp.capture.bundle.v1` sobre el JCS del bundle sin `signature`). */
export async function signBundle(
  parts: BundleParts,
  signer: CaptureSigner,
): Promise<CaptureBundle> {
  const unsigned = {
    session_id: parts.sessionId,
    sdk: { name: parts.sdk.name, version: parts.sdk.version, bundle_hash: parts.sdk.bundle_hash },
    device: parts.device,
    public_key: parts.publicKey,
    shots: parts.shots,
    videos: parts.videos,
    challenge: { schedule_id: parts.scheduleId, events: parts.events },
    declarations: parts.declarations,
  };
  return { ...unsigned, signature: await signer.sign(bundleMessage(unsigned)) };
}

const UNITS = new Set(['mm', 'cm', 'in']);

/** Texto corto del contrato (1..200), o nada. */
function short(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim().slice(0, 200);
  return s ? s : undefined;
}

function positive(v: unknown, max: number): number | undefined {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n > 0 && n <= max ? n : undefined;
}

/** Declaraciones del usuario normalizadas al contrato (peso en gramos; dims solo si están las 3). */
export function normalizeDeclarations(input: Record<string, unknown>): CaptureDeclarations {
  const out: CaptureDeclarations = {};
  const tracking = short(input.tracking);
  if (tracking) out.tracking = tracking;
  const weight = positive(input.weight, 10_000_000);
  if (weight !== undefined) out.weight = weight;
  const l = positive(input.l, 100_000);
  const w = positive(input.w, 100_000);
  const h = positive(input.h, 100_000);
  const unit = typeof input.unit === 'string' && UNITS.has(input.unit) ? input.unit : 'cm';
  if (l !== undefined && w !== undefined && h !== undefined)
    out.dims = { l, w, h, unit: unit as 'mm' | 'cm' | 'in' };
  const content = short(input.content);
  if (content) out.content = content;
  return out;
}
